import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { fmtEUR } from "@/lib/format";
import { TrendingUp, TrendingDown, Percent } from "lucide-react";

export default function Comptabilite() {
  const [stats, setStats] = useState(null);
  const [props, setProps] = useState([]);
  useEffect(() => {
    Promise.all([api.get("/dashboard/stats"), api.get("/properties")]).then(([s, p]) => {
      setStats(s.data); setProps(p.data);
    });
  }, []);

  if (!stats) return <div className="text-zinc-500 text-sm">Chargement…</div>;

  const totalCollected = stats.monthly_revenue.reduce((s, m) => s + m.encaisse, 0);
  const totalExpected = stats.monthly_revenue.reduce((s, m) => s + m.attendu, 0);
  const totalRentMonthly = props.reduce((s, p) => s + (p.status === "occupe" ? p.rent + p.charges : 0), 0);
  const annual = totalRentMonthly * 12;
  const totalAcquisition = props.reduce((s, p) => s + (p.acquisition_price || 0), 0);
  const yieldPct = totalAcquisition > 0 ? (annual / totalAcquisition * 100) : 0;

  return (
    <div className="space-y-6" data-testid="comptabilite-page">
      <div>
        <div className="text-overline">Comptabilité simplifiée</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{fontFamily:'Outfit'}}>Comptabilité</h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="card-flat p-6">
          <div className="text-overline">Loyers encaissés (12 mois)</div>
          <div className="data-value mt-3 text-emerald-700">{fmtEUR(totalCollected)}</div>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><TrendingUp className="w-3 h-3" /> sur {fmtEUR(totalExpected)} attendus</div>
        </Card>
        <Card className="card-flat p-6">
          <div className="text-overline">Revenu annuel (théorique)</div>
          <div className="data-value mt-3">{fmtEUR(annual)}</div>
          <div className="text-xs text-zinc-500 mt-1">Biens occupés × 12</div>
        </Card>
        <Card className="card-flat p-6">
          <div className="text-overline">Rentabilité brute</div>
          <div className="data-value mt-3 flex items-baseline gap-2">{yieldPct.toFixed(2)}<span className="text-base text-zinc-400">%</span></div>
          <div className="text-xs text-zinc-500 mt-1 flex items-center gap-1"><Percent className="w-3 h-3" /> sur {fmtEUR(totalAcquisition)} investis</div>
        </Card>
      </div>

      <Card className="card-flat p-6">
        <h3 className="text-lg font-semibold tracking-tight mb-4" style={{fontFamily:'Outfit'}}>Encaissé vs Attendu</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.monthly_revenue}>
              <CartesianGrid strokeDasharray="2 4" stroke="#F4F4F5" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#71717A" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}€`} />
              <Tooltip contentStyle={{ background: "#09090B", border: "none", borderRadius: 6, color: "white", fontSize: 12 }} formatter={(v) => `${v} €`} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="attendu" fill="#E4E4E7" radius={[4,4,0,0]} />
              <Bar dataKey="encaisse" fill="#18181B" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="card-flat p-6">
        <h3 className="text-lg font-semibold tracking-tight mb-4" style={{fontFamily:'Outfit'}}>Détail par bien</h3>
        <table className="w-full text-sm">
          <thead className="border-b border-zinc-200">
            <tr className="text-left text-xs uppercase tracking-wider text-zinc-500">
              <th className="px-2 py-2">Adresse</th><th className="px-2 py-2">Loyer mensuel</th>
              <th className="px-2 py-2">Loyer annuel</th><th className="px-2 py-2">Acquisition</th>
              <th className="px-2 py-2">Rentabilité brute</th>
            </tr>
          </thead>
          <tbody>
            {props.map(p => {
              const month = p.rent + p.charges;
              const yr = month * 12;
              const r = p.acquisition_price > 0 ? (yr / p.acquisition_price * 100).toFixed(2) : "—";
              return (
                <tr key={p.id} className="border-b border-zinc-100">
                  <td className="px-2 py-3">{p.address}</td>
                  <td className="px-2 py-3 mono">{fmtEUR(month)}</td>
                  <td className="px-2 py-3 mono">{fmtEUR(yr)}</td>
                  <td className="px-2 py-3 mono">{fmtEUR(p.acquisition_price)}</td>
                  <td className="px-2 py-3 mono font-medium">{r}{r !== "—" && "%"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
